import React, { useState, useEffect } from "react";
import { Form, Select, Input, InputNumber, Button, message, Spin } from "antd";
import apis from "../../apis";
import debounce from "lodash/debounce";
import "./index.scss";

const TocManualOrderPage = ({
  mode,
  productList,
  selectedProductId,
  onProductChange,
  id,
  record,
  type,
  onBack,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [topupPlanList, setTopupPlanList] = useState([]);
  const [seatList, setSeatList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detail, setDetail] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [seatLoading, setSeatLoading] = useState(false);

  const isEnterprise = mode === "enterprise";
  const isView = type === "view";

  // 获取企业席位列表
  const fetchSeatList = (tocCoId, prdId) => {
    if (!tocCoId) {
      setSeatList([]);
      return;
    }
    setSeatLoading(true);
    const params = { toc_co_id: tocCoId };
    // 如果有产品ID，则传入以筛选该产品的有效席位
    if (prdId) {
      params.prd_id = prdId;
    }
    apis
      .getTocEnterpriseSubAccountsByCompany(params)
      .then((res) => {
        setSeatLoading(false);
        if (res.code === 200) {
          const subscriptions = res.data?.subscriptions || [];
          const seats = [];
          subscriptions.forEach((subs) => {
            (subs.sub_accounts || []).forEach((seat) => {
              seats.push({
                sub_acct_num: subs.sub_acct_num,
                subs_plan_name: subs.subs_plan_name,
                ...seat
              });
            });
          });
          setSeatList(seats);
        }
      })
      .catch(() => {
        setSeatLoading(false);
        setSeatList([]);
      });
  };

  // 获取充值方案列表
  const fetchTopupPlanList = (prdId, planType) => {
    if (!prdId) {
      setTopupPlanList([]);
      return;
    }
    setPlanLoading(true);
    apis
      .getTocTopupPlanList({
        prd_id: prdId,
        topup_plan_type_code: planType,
        topup_plan_status_code: "LISTED",
        page: 1,
        page_size: 9999,
      })
      .then((res) => {
        setPlanLoading(false);
        if (res.code === 200) {
          const items = res.data?.items || [];
          setTopupPlanList(items);
        }
      })
      .catch(() => {
        setPlanLoading(false);
        setTopupPlanList([]);
      });
  };

  useEffect(() => {
    if (isView) {
      if (id) {
        apis.getTocOrderDetail({ order_id: id }).then((res) => {
          if (res.code === 200) {
            setDetail(res.data || {});
          }
        });
      }
      return;
    }
    form.resetFields();
    setSelectedPlan(null);
    setSeatList([]);
  }, [isEnterprise, form, isView, id]);

  // 企业选择变化时，清空席位和方案
  const handleCompanyChange = (tocCoId) => {
    setSeatList([]);
    setSelectedPlan(null);
    form.setFieldsValue({
      seat_id: undefined,
      topup_plan_id: undefined,
    });
    // 只有企业和产品都选择时才获取席位列表
    if (tocCoId && selectedProductId) {
      fetchSeatList(tocCoId, selectedProductId);
      fetchTopupPlanList(selectedProductId, "SEAT");
    }
  };

  // 用户选择变化时，获取方案列表
  const handleUserChange = (userId) => {
    setSelectedPlan(null);
    form.setFieldsValue({
      topup_plan_id: undefined,
    });
    if (userId && selectedProductId) {
      fetchTopupPlanList(selectedProductId, "PERSONAL");
    } else {
      setTopupPlanList([]);
    }
  };

  // 产品选择变化时，清空方案，重新获取席位（如有必要）
  const handleProductChange = (prdId) => {
    onProductChange(prdId);
    setSelectedPlan(null);
    form.setFieldsValue({
      topup_plan_id: undefined,
    });
    if (prdId) {
      const planType = isEnterprise ? "SEAT" : "PERSONAL";
      fetchTopupPlanList(prdId, planType);
      // 只有企业和产品都选择时才获取席位列表
      const currentCompanyId = form.getFieldValue("toc_co_id");
      if (isEnterprise && currentCompanyId) {
        setSeatList([]);
        form.setFieldsValue({ seat_id: undefined });
        fetchSeatList(currentCompanyId, prdId);
      }
    } else {
      // 产品清空时，清空席位列表
      setSeatList([]);
      form.setFieldsValue({ seat_id: undefined });
    }
  };

  // 方案变化时，更新表单字段
  const handlePlanChange = (planId) => {
    const plan = topupPlanList.find(
      (p) => p.topup_plan_id === planId
    );
    if (plan) {
      const latestVer = plan.listed_version || {};
      setSelectedPlan(plan);
      form.setFieldsValue({
        credits: latestVer.credits,
        original_price: latestVer.original_price,
        discount_price: latestVer.discount_price,
        topup_plan_desc: latestVer.topup_plan_desc,
        payment_amount: latestVer.discount_price || latestVer.original_price,
      });
    } else {
      setSelectedPlan(null);
      form.setFieldsValue({
        credits: undefined,
        original_price: undefined,
        discount_price: undefined,
        topup_plan_desc: undefined,
        payment_amount: undefined,
      });
    }
  };

  // 搜索企业
  const handleSearchCompany = React.useMemo(
    () =>
      debounce((value) => {
        if (!value) {
          setCompanyList([]);
          return;
        }
        apis
          .getTocCompanyList({ keyword: value, page: 1, page_size: 50 })
          .then((res) => {
            if (res.code === 200) {
              setCompanyList(res.data?.data || []);
            }
          })
          .catch(() => {
            setCompanyList([]);
          });
      }, 500),
    []
  );

  // 搜索用户
  const handleSearchUser = React.useMemo(
    () =>
      debounce((value) => {
        if (!value) {
          setUserList([]);
          return;
        }
        apis
          .searchTocUserByPhone({ phone: value })
          .then((res) => {
            if (res.code === 200) {
              setUserList(res.data || []);
            }
          })
          .catch(() => {
            setUserList([]);
          });
      }, 500),
    []
  );

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const latestVer = selectedPlan?.listed_version || {};
      const params = {
        topup_plan_id: values.topup_plan_id,
        ver_no: latestVer.ver_no,
        order_type: isEnterprise ? "ENTERPRISE" : "PERSONAL",
        prd_id: selectedProductId,
        topup_plan_name: latestVer.topup_plan_name,
        credits: latestVer.credits,
        original_price: latestVer.original_price,
        discount_price: latestVer.discount_price,
        topup_plan_desc: latestVer.topup_plan_desc || "",
        payment_amount: values.payment_amount,
      };

      if (isEnterprise) {
        params.toc_co_id = values.toc_co_id;
        params.seat_id = values.seat_id;
      } else {
        params.user_id = values.user_id;
      }

      setSubmitLoading(true);
      apis
        .tocManualTopup(params)
        .then((res) => {
          setSubmitLoading(false);
          if (res.code === 200) {
            message.success("下单成功");
            onSuccess();
          } else {
            message.error(res.message);
          }
        })
        .catch(() => {
          setSubmitLoading(false);
        });
    });
  };

  const displayData = { ...record, ...detail };

  // 判断是企业订单还是个人订单（仅用于查看模式）
  const isEnterpriseOrder = displayData.toc_co_id && displayData.toc_co_id !== "";
  const isPersonalOrder = displayData.co_user_id && displayData.co_user_id !== "";
  
  // 查看模式下根据数据判断显示什么；编辑模式下根据mode判断
  const showEnterpriseSelect = isView ? isEnterpriseOrder : isEnterprise;
  const showPersonalSelect = isView ? isPersonalOrder : !isEnterprise;

  return (
    <div className="manualTopupPage">
      <div className="content-body">
        <div className="itemTitle">
          <span className="backIconSpan" onClick={onBack}>
            <span className="titleLine"></span>
          </span>
          订单信息
        </div>

        <div className="itemTitle2">
          <span>
            01<i></i>
          </span>
          基本信息
        </div>

        <Form
          form={form}
          layout="horizontal"
          labelCol={{ flex: "140px" }}
          wrapperCol={{ flex: "auto" }}
          labelAlign="left"
          colon={false}
        >
          <div className="subscriptionItemsContent">
            {/* 企业/用户选择 */}
            {showEnterpriseSelect ? (
              <Form.Item
                name="toc_co_id"
                label="企业"
                rules={[{ required: !isView, message: "请选择企业" }]}
              >
                {isView ? (
                  <span className="view-text">
                    {displayData.co_name || "-"}
                    {displayData.co_code ? ` (${displayData.co_code})` : ""}
                  </span>
                ) : (
                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    placeholder="输入所有者手机号或企业名称、编号搜索"
                    onSearch={handleSearchCompany}
                    filterOption={false}
                    notFoundContent={null}
                    onChange={handleCompanyChange}
                    labelRender={
                      (option) => {
                          const info = JSON.parse(option.label || '');
                          return (
                              <div className="PersonAndCompanySelectItem3">
                                  <a className="nameBox" title={info.toc_co_name}>
                                      <div className='nameBoxContent' style={{ fontWeight: 'unset' }}>{info.toc_co_name}</div>
                                  </a>
                                  <span className="codeBox">企业编号:{info.toc_co_code}</span>
                              </div>
                          )
                      }
                  }
                  optionRender={(option) => {
                      const info = JSON.parse(option.label || '');
                      return (
                        <div>
                          <div className="PersonAndCompanySelectItem3">
                              <a className="nameBox" title={info.toc_co_name}>
                                  <div className='nameBoxContent'>{info.toc_co_name}</div>
                              </a>
                              <span className="codeBox">企业编号:{info.toc_co_code}</span>
                          </div>
                          <div className='ownerBox'>所有者:{info.user_phone}/{info.user_name}</div>
                        </div>
                      )
                  }}
                    options={companyList.map((c) => ({
                      value: c.toc_co_id,
                      label: JSON.stringify(c),
                    }))}
                  />
                )}
              </Form.Item>
            ) : showPersonalSelect ? (
              <Form.Item
                name="user_id"
                label="用户"
                rules={[{ required: !isView, message: "请选择用户" }]}
              >
                {isView ? (
                  <span className="view-text">
                    {displayData.user_phone || displayData.nickname || "-"}
                    {displayData.nickname ? ` / ${displayData.nickname}` : ""}
                  </span>
                ) : (
                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    placeholder="请输入手机号查询"
                    onSearch={handleSearchUser}
                    filterOption={false}
                    notFoundContent={null}
                    onChange={handleUserChange}
                    options={userList.map((u) => ({
                      value: u.user_id,
                      label: (
                        <div className="PersonAndCompanySelectItem2">
                          <span
                            className="mobileBox"
                            title={u.mobile_no || u.phone}
                          >
                            {u.mobile_no || u.phone}
                          </span>
                          <span
                            className="nameBox"
                            title={u.user_nickname || u.nickname || u.show_name}
                            style={{ textAlign: "right" }}
                          >
                            {u.user_nickname || u.nickname || u.show_name}
                          </span>
                        </div>
                      ),
                    }))}
                  />
                )}
              </Form.Item>
            ) : null}

            <Form.Item label="">
              <></>
            </Form.Item>

            {/* 订单类型 */}
            <Form.Item label="订单类型">
              <span className="view-text">积分充值</span>
            </Form.Item>

            <Form.Item label="">
              <></>
            </Form.Item>

            {/* 产品 */}
            <Form.Item
              name="prd_id"
              label="产品"
              rules={[{ required: !isView, message: "请选择产品" }]}
            >
              {isView ? (
                <span className="view-text">
                  {displayData.product_name || displayData.prd_name || "-"}
                </span>
              ) : (
                <Select
                  style={{ width: "100%" }}
                  placeholder="请选择产品"
                  value={selectedProductId}
                  onChange={handleProductChange}
                  options={productList.map((p) => ({
                    value: p.prd_id,
                    label: p.prd_name,
                  }))}
                />
              )}
            </Form.Item>

            {/* 充值方案 */}
            <Form.Item
              name="topup_plan_id"
              label="充值方案名称"
              rules={[{ required: !isView, message: "请选择充值方案" }]}
            >
              {isView ? (
                <span className="view-text">
                  {displayData.topup_plan_name || displayData.order_name || "-"}
                </span>
              ) : (
                <Select
                  style={{ width: "100%" }}
                  placeholder="请选择充值方案"
                  onChange={handlePlanChange}
                  loading={planLoading}
                  disabled={
                    !selectedProductId ||
                    (isEnterprise && !form.getFieldValue("toc_co_id"))
                  }
                  options={topupPlanList.map((p) => ({
                    value: p.topup_plan_id,
                    label: (
                      <span>
                        {p?.listed_version?.topup_plan_name}
                        <span style={{ color: "#999", fontSize: "12px" }}>
                          {" "}
                          (v{p.list_ver_no?.replace('v', '') || p.last_ver_no?.replace('v', '')})
                        </span>
                      </span>
                    ),
                  }))}
                />
              )}
            </Form.Item>

            {/* 席位（企业版才有） */}
            {isEnterprise && (
              <Form.Item
                name="seat_id"
                label="席位"
                rules={[{ required: !isView, message: "请选择席位" }]}
              >
                {isView ? (
                  <span className="view-text">
                    {displayData.seat_id ? `席位ID: ${displayData.seat_id}` : "-"}
                  </span>
                ) : (
                  <Select
                    style={{ width: "100%" }}
                    placeholder={
                      !form.getFieldValue("toc_co_id") || !selectedProductId
                        ? "请先选择企业和产品"
                        : "请选择席位"
                    }
                    loading={seatLoading}
                    disabled={!form.getFieldValue("toc_co_id") || !selectedProductId}
                    options={seatList.map((s) => ({
                      value: s.toc_co_sub_subs_id,
                      label: (
                        <div className="PersonAndCompanySelectItem2">
                          <span className="mobileBox" title={s.subs_plan_name + '*' + s.sub_acct_num}>
                            {s.subs_plan_name + '*' + s.sub_acct_num}
                          </span>
                          <span
                            className="nameBox"
                            title={
                              s.user_phone !== "-"
                                ? `${s.user_phone}${
                                    s.user_nickname !== "-"
                                      ? ` / ${s.user_nickname}`
                                      : ""
                                  }`
                                : `席位${s.seq}`
                            }
                            style={{ textAlign: "right" }}
                          >
                            {s.user_phone !== "-"
                              ? `${s.user_phone}${
                                  s.user_nickname !== "-"
                                    ? ` / ${s.user_nickname}`
                                    : ""
                                }`
                              : `席位${s.seq}`}
                          </span>
                        </div>
                      ),
                    }))}
                  />
                )}
              </Form.Item>
            )}
            
            {
              isEnterprise && (<Form.Item label="">
                <></>
              </Form.Item>)
            }
            
            {/* 积分 */}
            <Form.Item label="积分">
              <span className="view-text">
                {(selectedPlan?.listed_version?.credits || displayData.credits)
                  ? parseInt(selectedPlan?.listed_version?.credits || displayData.credits).toLocaleString()
                  : "-"}
              </span>
            </Form.Item>

            <Form.Item label="">
              <></>
            </Form.Item>

            {/* 原价 */}
            <Form.Item label="原价(元)">
              <span className="view-text">
                {selectedPlan?.listed_version?.original_price || displayData.original_price
                  ? parseFloat(selectedPlan?.listed_version?.original_price || displayData.original_price).toFixed(2)
                  : "-"}
              </span>
            </Form.Item>

            {/* 折扣价 */}
            <Form.Item label="折扣价(元)">
              <span className="view-text">
                {selectedPlan?.listed_version?.discount_price || displayData.discount_price
                  ? parseFloat(selectedPlan?.listed_version?.discount_price || displayData.discount_price).toFixed(2)
                  : "-"}
              </span>
            </Form.Item>

            {/* 方案描述 */}
            <Form.Item label="方案描述">
              <span className="view-text">
                {selectedPlan?.listed_version?.topup_plan_desc ||
                  selectedPlan?.topup_plan_name ||
                  displayData.topup_plan_desc ||
                  displayData.summary ||
                  "-"}
              </span>
            </Form.Item>
            
            <Form.Item label="">
              <></>
            </Form.Item>

            {/* 支付金额 */}
            <Form.Item
              name="payment_amount"
              label="支付金额(元)"
              rules={[{ required: !isView, message: "请输入支付金额" }]}
            >
              {isView ? (
                <span className="view-text">
                  {displayData.payment_amount
                    ? parseFloat(displayData.payment_amount).toFixed(2)
                    : "-"}
                </span>
              ) : (
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="请输入支付金额"
                  min={0}
                  precision={2}
                />
              )}
            </Form.Item>

            {/* 查看详情时的额外字段 */}
            {isView && (
              <>
                <Form.Item label="订单编号">
                  <span className="view-text">
                    {displayData.order_code || "-"}
                  </span>
                </Form.Item>
                <Form.Item label="订单状态">
                  <span className="view-text">
                    {displayData.order_status_code === "COMPLETED"
                      ? "已完成"
                      : displayData.order_status_code === "CANCELED"
                      ? "已撤销"
                      : displayData.order_status_code || "-"}
                  </span>
                </Form.Item>
                <Form.Item label="下单时间">
                  <span className="view-text">
                    {displayData.order_time || "-"}
                  </span>
                </Form.Item>
              </>
            )}
          </div>
        </Form>
      </div>

      <div className="footer-actions">
        {isView ? (
          <Button type="primary" onClick={onBack}>
            关闭
          </Button>
        ) : (
          <>
            <Button onClick={onBack}>取消</Button>
            <Button
              style={{ marginLeft: "24px" }}
              type="primary"
              loading={submitLoading}
              onClick={handleSubmit}
            >
              提交订单
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TocManualOrderPage;
